import React, { useEffect, useState, useRef } from 'react';
import firebase from 'firebase/compat/app';
import '../styles/campaignMap.css';
import i18next from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faEye,
  faPencilAlt,
  faUndo,
  faTrash,
  faEraser,
  faChessRook,
  faCrosshairs,
  faSkull,
  faTree,
  faMountain,
  faHome,
  faStar,
  faExclamationTriangle,
  faQuestionCircle,
  faDragon,
  faAnchor,
  faFlask,
  faScroll,
  faDoorOpen,
} from '@fortawesome/free-solid-svg-icons';
import MapCollab from './MapCollab';
import { resolveActiveMap } from '../utils/campaignMapPaths';

export const PIN_ICONS = [
  { key: 'mapMarkerAlt', icon: faMapMarkerAlt, label: 'Lieu' },
  { key: 'chessRook', icon: faChessRook, label: 'Château' },
  { key: 'crosshairs', icon: faCrosshairs, label: 'Combat' },
  { key: 'skull', icon: faSkull, label: 'Danger' },
  { key: 'tree', icon: faTree, label: 'Forêt' },
  { key: 'mountain', icon: faMountain, label: 'Montagne' },
  { key: 'home', icon: faHome, label: 'Village' },
  { key: 'star', icon: faStar, label: 'Important' },
  { key: 'exclamationTriangle', icon: faExclamationTriangle, label: 'Alerte' },
  { key: 'questionCircle', icon: faQuestionCircle, label: 'Inconnu' },
  { key: 'dragon', icon: faDragon, label: 'Monstre' },
  { key: 'anchor', icon: faAnchor, label: 'Port' },
  { key: 'flask', icon: faFlask, label: 'Alchimie' },
  { key: 'scroll', icon: faScroll, label: 'Quête' },
  { key: 'doorOpen', icon: faDoorOpen, label: 'Entrée' },
];

const getIconDef = (key) => PIN_ICONS.find(p => p.key === key)?.icon || faMapMarkerAlt;

const CampaignMap = ({ campaignUid, isDm = false, user = null, characterName = '' }) => {
  const [activeMap, setActiveMap] = useState(null); // { url, path, pins, ... }
  const [pins, setPins] = useState([]);
  const [selectedPin, setSelectedPin] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [toolMode, setToolMode] = useState('view'); // 'view' | 'addPin' | 'draw'
  const collabRef = useRef(null);

  useEffect(() => {
    if (!campaignUid) return;
    const ref = firebase.database().ref(`campaignMap/${campaignUid}`);
    ref.on('value', snap => {
      const active = resolveActiveMap(snap.val(), campaignUid);
      setActiveMap(active);
      setPins(active?.pins ? Object.entries(active.pins).map(([id, p]) => ({ id, ...p })) : []);
      setSelectedPin(null); // fermer une tooltip qui pointerait vers l'ancienne carte
    });
    return () => ref.off();
  }, [campaignUid]);

  const visiblePins = isDm ? pins : pins.filter(p => !p.hidden);

  if (!activeMap?.url) {
    return (
      <div className="mapEmpty">
        <span>{i18next.t('map.noMap')}</span>
      </div>
    );
  }

  return (
    <div className="mapContainer">
      {user && (
        <div className="mapCollabToolbar">
          <div className="mapToolModes">
            <button
              className={`mapCollabTool${toolMode === 'view' ? ' active' : ''}`}
              onClick={() => setToolMode('view')}
            >
              <FontAwesomeIcon icon={faEye} /> {i18next.t('map.toolView')}
            </button>
            <button
              className={`mapCollabTool${toolMode === 'addPin' ? ' active' : ''}`}
              onClick={() => setToolMode(m => (m === 'addPin' ? 'view' : 'addPin'))}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} /> {i18next.t('map.toolAddPin')}
            </button>
            <button
              className={`mapCollabTool${toolMode === 'draw' ? ' active' : ''}`}
              onClick={() => setToolMode(m => (m === 'draw' ? 'view' : 'draw'))}
            >
              <FontAwesomeIcon icon={faPencilAlt} /> {i18next.t('map.toolDraw')}
            </button>
            <button
              className={`mapCollabTool${toolMode === 'erase' ? ' active' : ''}`}
              onClick={() => setToolMode(m => (m === 'erase' ? 'view' : 'erase'))}
            >
              <FontAwesomeIcon icon={faEraser} /> {i18next.t('map.toolErase')}
            </button>
          </div>

          {(toolMode === 'draw' || toolMode === 'addPin' || toolMode === 'erase') && (
            <div className="mapToolContext">
              {toolMode === 'draw' && (
                <>
                  <button className="mapCollabTool" onClick={() => collabRef.current?.undo()}>
                    <FontAwesomeIcon icon={faUndo} /> {i18next.t('map.undo')}
                  </button>
                  <button className="mapCollabTool" onClick={() => collabRef.current?.clearMine()}>
                    <FontAwesomeIcon icon={faTrash} /> {i18next.t('map.clearMine')}
                  </button>
                </>
              )}
              <span className="mapCollabHint">
                {toolMode === 'addPin' && i18next.t('map.clickToAddPlayer')}
                {toolMode === 'draw' && i18next.t('map.drawHint')}
                {toolMode === 'erase' && i18next.t('map.eraseHint')}
              </span>
            </div>
          )}
        </div>
      )}
      <div className="mapWrapper">
        <div className="mapImageContainer">
          <img src={activeMap.url} alt="carte" className="mapImage" draggable={false} />
          {visiblePins.map(pin => (
            <button
              key={pin.id}
              className={`mapPin${pin.hidden ? ' mapPinHidden' : ''}`}
              style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const above = rect.top > 220;
                setTooltipPos({ x: rect.left + rect.width / 2, y: above ? rect.top : rect.bottom, above });
                setSelectedPin(selectedPin?.id === pin.id ? null : pin);
              }}
              title={pin.label}
            >
              <FontAwesomeIcon icon={getIconDef(pin.icon)} />
            </button>
          ))}
          {user && (
            <MapCollab
              ref={collabRef}
              mapPath={activeMap.path}
              user={user}
              authorName={characterName}
              toolMode={toolMode}
              onConsumeTool={() => setToolMode('view')}
            />
          )}
        </div>
      </div>
      {selectedPin && (
        <div
          className="mapPinTooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y, transform: tooltipPos.above ? 'translate(-50%, calc(-100% - 0.75rem))' : 'translate(-50%, 0.75rem)' }}
        >
          <button className="mapTooltipClose" onClick={() => setSelectedPin(null)}>×</button>
          <div className="mapTooltipIcon">
            <FontAwesomeIcon icon={getIconDef(selectedPin.icon)} />
          </div>
          <strong className="mapTooltipLabel">{selectedPin.label}</strong>
          {selectedPin.description && (
            <p className="mapTooltipDesc">{selectedPin.description}</p>
          )}
          {isDm && (
            <span className={`mapTooltipVisibility${selectedPin.hidden ? ' hidden' : ''}`}>
              {selectedPin.hidden ? i18next.t('map.hidden') : i18next.t('map.visible')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignMap;
